/**
 * Pure tree algebra for dees-mosaic layouts. No DOM access; unit-testable in
 * node. Every mutation helper returns a NEW normalized layout; inputs are
 * never mutated.
 *
 * Normalization invariants:
 * - a split with a single child collapses to that child
 * - nested splits with the same orientation merge into the parent
 * - ratios.length === children.length, summing to 1, each held at or near
 *   the minimum share (single-pass redistribution may undershoot slightly)
 * - an empty tree is { root: null }
 */
import type {
  IMosaicLayout,
  IMosaicLeafNode,
  IMosaicSplitNode,
  TMosaicDropEdge,
  TMosaicNode,
  TMosaicOrientation,
} from './types.js';

const minRatio = 0.05;

let idCounter = 0;

export const createMosaicId = (prefix: string): string => {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter.toString(36)}`;
};

export const createMosaicLeaf = (surface: string, id?: string): IMosaicLeafNode => ({
  type: 'leaf',
  id: id ?? createMosaicId('leaf'),
  surface,
});

const cloneNode = (node: TMosaicNode): TMosaicNode => {
  if (node.type === 'leaf') return { ...node };
  return {
    ...node,
    children: node.children.map(cloneNode),
    ratios: [...node.ratios],
  };
};

const normalizeRatios = (ratios: number[], count: number): number[] => {
  let next = ratios.slice(0, count);
  while (next.length < count) next.push(1 / count);
  next = next.map((ratio) => (Number.isFinite(ratio) && ratio > 0 ? ratio : 1 / count));
  const sum = next.reduce((total, ratio) => total + ratio, 0);
  next = next.map((ratio) => ratio / sum);
  // clamp to the minimum share, re-distributing the deficit proportionally
  const deficitIndices = next.map((ratio, index) => (ratio < minRatio ? index : -1)).filter((index) => index >= 0);
  if (deficitIndices.length && deficitIndices.length < count) {
    const deficit = deficitIndices.reduce((total, index) => total + (minRatio - next[index]), 0);
    const donorSum = next.reduce((total, ratio, index) => (deficitIndices.includes(index) ? total : total + ratio), 0);
    next = next.map((ratio, index) => {
      if (deficitIndices.includes(index)) return minRatio;
      return ratio - (deficit * ratio) / donorSum;
    });
  }
  return next;
};

const normalizeNode = (node: TMosaicNode): TMosaicNode | null => {
  if (node.type === 'leaf') return { ...node };
  const flattenedChildren: TMosaicNode[] = [];
  const flattenedRatios: number[] = [];
  node.children.forEach((child, index) => {
    const normalizedChild = normalizeNode(child);
    if (!normalizedChild) return;
    const ratio = node.ratios[index] ?? 1 / node.children.length;
    if (normalizedChild.type === 'split' && normalizedChild.orientation === node.orientation) {
      // merge same-orientation splits: children inherit a share of the slot
      normalizedChild.children.forEach((grandChild, grandIndex) => {
        flattenedChildren.push(grandChild);
        flattenedRatios.push(ratio * normalizedChild.ratios[grandIndex]);
      });
      return;
    }
    flattenedChildren.push(normalizedChild);
    flattenedRatios.push(ratio);
  });
  if (!flattenedChildren.length) return null;
  if (flattenedChildren.length === 1) return flattenedChildren[0];
  return {
    type: 'split',
    id: node.id,
    orientation: node.orientation,
    children: flattenedChildren,
    ratios: normalizeRatios(flattenedRatios, flattenedChildren.length),
  };
};

export const mosaicNormalize = (layout: IMosaicLayout): IMosaicLayout => {
  if (!layout.root) return { root: null };
  return { root: normalizeNode(cloneNode(layout.root)) };
};

export const mosaicLeaves = (layout: IMosaicLayout): IMosaicLeafNode[] => {
  const leaves: IMosaicLeafNode[] = [];
  const walk = (node: TMosaicNode | null): void => {
    if (!node) return;
    if (node.type === 'leaf') {
      leaves.push(node);
      return;
    }
    node.children.forEach(walk);
  };
  walk(layout.root);
  return leaves;
};

export const mosaicFindLeafBySurface = (
  layout: IMosaicLayout,
  surface: string,
): IMosaicLeafNode | undefined => mosaicLeaves(layout).find((leaf) => leaf.surface === surface);

export const mosaicFindLeaf = (
  layout: IMosaicLayout,
  leafId: string,
): IMosaicLeafNode | undefined => mosaicLeaves(layout).find((leaf) => leaf.id === leafId);

/** Removes a leaf; collapsing/merging is handled by normalization. */
export const mosaicRemove = (layout: IMosaicLayout, leafId: string): IMosaicLayout => {
  const removeFrom = (node: TMosaicNode): TMosaicNode | null => {
    if (node.type === 'leaf') return node.id === leafId ? null : node;
    const children: TMosaicNode[] = [];
    const ratios: number[] = [];
    node.children.forEach((child, index) => {
      const kept = removeFrom(child);
      if (!kept) return;
      children.push(kept);
      ratios.push(node.ratios[index]);
    });
    if (!children.length) return null;
    return { ...node, children, ratios };
  };
  if (!layout.root) return { root: null };
  const root = removeFrom(cloneNode(layout.root));
  return mosaicNormalize({ root });
};

const edgeOrientation = (edge: TMosaicDropEdge): TMosaicOrientation =>
  edge === 'left' || edge === 'right' ? 'row' : 'column';

const edgeBefore = (edge: TMosaicDropEdge): boolean => edge === 'left' || edge === 'top';

/**
 * Inserts a leaf next to targetLeafId on the given edge (splitting the target
 * 50/50 along the edge's axis). targetLeafId null docks against the whole
 * layout (or becomes the root of an empty one).
 */
export const mosaicInsert = (
  layout: IMosaicLayout,
  targetLeafId: string | null,
  edge: TMosaicDropEdge,
  leaf: IMosaicLeafNode,
): IMosaicLayout => {
  if (!layout.root) return { root: { ...leaf } };
  const orientation = edgeOrientation(edge);
  const before = edgeBefore(edge);

  const wrap = (node: TMosaicNode): IMosaicSplitNode => ({
    type: 'split',
    id: createMosaicId('split'),
    orientation,
    children: before ? [{ ...leaf }, node] : [node, { ...leaf }],
    ratios: [0.5, 0.5],
  });

  if (targetLeafId === null) {
    return mosaicNormalize({ root: wrap(cloneNode(layout.root)) });
  }

  const insertInto = (node: TMosaicNode): TMosaicNode => {
    if (node.type === 'leaf') {
      return node.id === targetLeafId ? wrap(node) : node;
    }
    return { ...node, children: node.children.map(insertInto) };
  };
  return mosaicNormalize({ root: insertInto(cloneNode(layout.root)) });
};

/** Transfers ratio between two adjacent siblings of a split. */
export const mosaicResize = (
  layout: IMosaicLayout,
  splitId: string,
  dividerIndex: number,
  ratioDelta: number,
): IMosaicLayout => {
  const apply = (node: TMosaicNode): TMosaicNode => {
    if (node.type === 'leaf') return node;
    if (node.id !== splitId) {
      return { ...node, children: node.children.map(apply) };
    }
    const ratios = [...node.ratios];
    const leftIndex = dividerIndex;
    const rightIndex = dividerIndex + 1;
    if (leftIndex < 0 || rightIndex >= ratios.length) return node;
    const pair = ratios[leftIndex] + ratios[rightIndex];
    const nextLeft = Math.min(Math.max(ratios[leftIndex] + ratioDelta, minRatio), pair - minRatio);
    ratios[leftIndex] = nextLeft;
    ratios[rightIndex] = pair - nextLeft;
    return { ...node, ratios };
  };
  if (!layout.root) return { root: null };
  return { root: apply(cloneNode(layout.root)) };
};

/** Equalizes the ratios of one split (or every split when splitId omitted). */
export const mosaicEqualize = (layout: IMosaicLayout, splitId?: string): IMosaicLayout => {
  const apply = (node: TMosaicNode): TMosaicNode => {
    if (node.type === 'leaf') return node;
    const children = node.children.map(apply);
    if (!splitId || node.id === splitId) {
      return { ...node, children, ratios: children.map(() => 1 / children.length) };
    }
    return { ...node, children };
  };
  if (!layout.root) return { root: null };
  return { root: apply(cloneNode(layout.root)) };
};

/** Moves an existing leaf next to another leaf (drag-and-dock commit). */
export const mosaicMove = (
  layout: IMosaicLayout,
  leafId: string,
  targetLeafId: string,
  edge: TMosaicDropEdge,
): IMosaicLayout => {
  if (leafId === targetLeafId) return mosaicNormalize(layout);
  const leaf = mosaicFindLeaf(layout, leafId);
  if (!leaf) return mosaicNormalize(layout);
  const without = mosaicRemove(layout, leafId);
  // the target may have been the leaf's only sibling — it still exists
  if (!mosaicFindLeaf(without, targetLeafId)) return mosaicNormalize(layout);
  return mosaicInsert(without, targetLeafId, edge, { ...leaf });
};
